import { useState } from 'react';
import { AlertEmailsPanel } from '../components/AlertEmailsPanel/AlertEmailsPanel.js';
import { AdminThreadViewer } from '../components/Analyst/AdminThreadViewer';
import { ApiError, api } from '../api/client.js';
import type { DirectoryUser, Me } from '../api/types.js';
import { DataTable, type Column } from '../components/DataTable/DataTable.js';
import { ErrorBanner, Skeleton } from '../components/States/States.js';
import { Header } from '../shell/Header.js';
import { useAsync } from '../hooks/useAsync.js';
import { TTL_MS } from '../store/dataStore.js';
import { useStoreActions, useStoreStatus } from '../store/StoreProvider.js';
import { color, fontSize, radius } from '../theme/tokens.js';

/**
 * Settings — user management (FR-042 – FR-048, gate 1, gate 9).
 *
 * The blueprint has no Settings page, so this is composed from blueprint primitives — the same
 * shell, tokens, control styling, and the shared `<DataTable>`. No second visual language.
 *
 * **There is no "add user" control** (FR-048), and its absence is not an omission: accounts
 * enter the directory only by first sign-in (FR-004). A create control would let an admin
 * pre-seed a record, which would then collide with auto-provisioning's insert-if-absent rule
 * and could resurrect a deactivated email.
 *
 * `activeAdminCount` disables the controls that would trip `LAST_ADMIN_PROTECTED` *before* the
 * click. The server still refuses regardless (FR-045) — this is courtesy, never the control.
 *
 * **Access requests come first** (owner request 2026-10-06). A first sign-in no longer grants
 * access; it files a request that waits here, so the queue is the one thing on this page someone
 * is blocked on. It reads the response's own `pending` list rather than filtering `users`,
 * because `users` is one alphabetical page and a request could sit on a page nobody opens.
 */

export interface SettingsProps {
  me: Me;
}

export function Settings({ me }: SettingsProps) {
  const [pending, setPending] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  const directory = useAsync(() => api.users(), []);
  const users = directory.data?.users ?? [];
  const requests = directory.data?.pending ?? [];
  const activeAdminCount = directory.data?.activeAdminCount ?? 0;

  async function mutate(email: string, run: () => Promise<unknown>) {
    setPending(email);
    setActionError(null);
    try {
      await run();
      directory.reload();
    } catch (err) {
      setActionError(
        err instanceof ApiError
          ? err.message
          : 'The change could not be applied. Please try again.',
      );
    } finally {
      setPending(null);
    }
  }

  /** True when this row is the last thing standing between the org and a lockout. */
  function isLastActiveAdmin(user: DirectoryUser): boolean {
    return user.role === 'admin' && user.isActive && activeAdminCount <= 1;
  }

  const columns: Array<Column<DirectoryUser>> = [
    {
      key: 'email',
      label: 'ACCOUNT',
      fullLabel: 'Directory account — email and display name',
      width: '2fr',
      align: 'left',
      render: (user) => (
        <div style={{ padding: '10px 14px', minWidth: 0 }}>
          <div
            style={{
              fontSize: fontSize.bodyStrong,
              fontWeight: 600,
              color: color.ink,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {user.displayName ?? user.email}
            {user.email === me.email ? (
              <span style={{ marginLeft: 6, fontSize: '9.5px', color: color.inkFaint }}>
                YOU
              </span>
            ) : null}
          </div>
          <div style={{ fontSize: fontSize.caption, color: color.inkFaint }}>{user.email}</div>
        </div>
      ),
      sortValue: (user) => user.email,
    },
    {
      key: 'role',
      label: 'ROLE',
      fullLabel: 'Role — user or admin. Both have identical data access.',
      width: '110px',
      align: 'left',
      render: (user) => (
        <div style={{ padding: '10px 8px', fontSize: fontSize.body, color: color.inkRow }}>
          {user.role === 'admin' ? 'Administrator' : 'User'}
        </div>
      ),
      sortValue: (user) => user.role,
    },
    {
      key: 'status',
      label: 'STATUS',
      fullLabel:
        'Access status — awaiting approval until an admin grants it; a deactivated account is refused on its next request',
      width: '150px',
      align: 'left',
      render: (user) => (
        <div style={{ padding: '10px 8px' }}>
          <StatusPill user={user} />
        </div>
      ),
      sortValue: (user) => (user.isActive ? 2 : user.awaitingApproval ? 1 : 0),
    },
    {
      key: 'firstSeenAt',
      label: 'FIRST SEEN',
      fullLabel: 'First seen — when this account first signed in',
      width: '130px',
      align: 'left',
      render: (user) => (
        <div style={{ padding: '10px 8px', fontSize: fontSize.body, color: color.inkMuted }}>
          {formatDate(user.firstSeenAt)}
        </div>
      ),
      sortValue: (user) => user.firstSeenAt,
    },
    {
      key: 'actions',
      label: 'ACTIONS',
      fullLabel: 'Change role, grant access, or deactivate and reactivate it',
      width: '260px',
      align: 'left',
      sortable: false,
      render: (user) => {
        const guarded = isLastActiveAdmin(user);
        const busy = pending === user.email;

        return (
          <div style={{ padding: '8px', display: 'flex', gap: 8, alignItems: 'center' }}>
            <button
              type="button"
              disabled={guarded || busy}
              onClick={() =>
                mutate(user.email, () =>
                  api.changeRole(user.email, user.role === 'admin' ? 'user' : 'admin'),
                )
              }
              title={
                guarded
                  ? 'This is the last active administrator — the change would leave nobody able to administer the system.'
                  : undefined
              }
              style={buttonStyle(guarded || busy)}
            >
              {user.role === 'admin' ? 'Make user' : 'Make admin'}
            </button>

            <button
              type="button"
              disabled={(guarded && user.isActive) || busy}
              onClick={() =>
                mutate(user.email, () => api.changeStatus(user.email, !user.isActive))
              }
              title={
                guarded && user.isActive
                  ? 'This is the last active administrator — deactivating them would lock everyone out of Settings.'
                  : undefined
              }
              style={buttonStyle((guarded && user.isActive) || busy)}
            >
              {user.isActive
                ? 'Deactivate'
                : user.awaitingApproval
                  ? 'Grant access'
                  : 'Reactivate'}
            </button>
          </div>
        );
      },
    },
  ];

  return (
    <>
      {/* Settings carries directory state, not snapshot data, so there is no as-of date to
          show — an `asOf` here would be meaningless (contracts/settings.md). */}
      <Header
        crumb="CYNET Health Commercial"
        title="Settings"
        asOf={null}
        syncStatus="healthy"
        staleness={null}
      />

      <div style={{ padding: '22px 30px 60px' }}>
        <AccessRequests
          requests={requests}
          loading={directory.loading && !directory.data}
          busyEmail={pending}
          onAnswer={(email, grant) => mutate(email, () => api.changeStatus(email, grant))}
        />

        <DataPanel />
        {/* Spec 008: the third admin capability — alert email counts, read-only. */}
        <AlertEmailsPanel />

        <p
          style={{
            margin: '0 0 16px',
            fontSize: fontSize.bodyStrong,
            color: color.inkMuted,
            maxWidth: 720,
            lineHeight: 1.5,
          }}
        >
          Everyone who has signed in appears here. Accounts are created by first sign-in from an
          allowed domain and wait for approval before they can see any data — there is no way to
          add one by hand, and a deactivated account cannot restore itself by signing in again.
        </p>

        {directory.error ? (
          <div style={{ marginBottom: 16 }}>
            <ErrorBanner
              message={`Could not load the directory. ${directory.error.message}`}
              onRetry={directory.reload}
            />
          </div>
        ) : null}

        {actionError ? (
          <div style={{ marginBottom: 16 }}>
            <ErrorBanner message={actionError} />
          </div>
        ) : null}

        {activeAdminCount <= 1 ? (
          <div
            role="status"
            style={{
              marginBottom: 16,
              background: color.warningBg,
              border: `1px solid ${color.warningBorder}`,
              color: color.noticeInk,
              borderRadius: radius['3xl'],
              padding: '10px 16px',
              fontSize: fontSize.captionStrong,
            }}
          >
            There is only one active administrator. Their role and status controls are disabled
            — the system refuses any change that would leave nobody able to administer it.
          </div>
        ) : null}

        {directory.loading && users.length === 0 ? (
          <Skeleton height={280} />
        ) : (
          <DataTable<DirectoryUser>
            caption="Directory accounts"
            columns={columns}
            rows={users}
            rowKey={(user) => user.email}
            minWidth="900px"
            emptyMessage="Nobody has signed in yet."
          />
        )}
      </div>
    </>
  );
}

function StatusPill({ user }: { user: DirectoryUser }) {
  const tone = user.isActive
    ? {
        label: 'Active',
        ink: color.inkMintStrong,
        bg: color.washMint,
        border: color.borderMint,
      }
    : user.awaitingApproval
      ? {
          label: 'Awaiting approval',
          ink: color.noticeInk,
          bg: color.warningBg,
          border: color.warningBorder,
        }
      : {
          label: 'Deactivated',
          ink: color.inkMuted,
          bg: color.surfaceChip,
          border: color.borderChip,
        };

  return (
    <span
      style={{
        fontSize: fontSize.caption,
        fontWeight: 600,
        padding: '2px 8px',
        borderRadius: radius.pill,
        whiteSpace: 'nowrap',
        color: tone.ink,
        background: tone.bg,
        border: `1px solid ${tone.border}`,
      }}
    >
      {tone.label}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Access requests
// ---------------------------------------------------------------------------

interface AccessRequestsProps {
  requests: readonly DirectoryUser[];
  loading: boolean;
  busyEmail: string | null;
  onAnswer: (email: string, grant: boolean) => void;
}

/**
 * The approval queue. Granting is `isActive: true`; declining keeps the account inactive and
 * takes it out of the queue, so the person is told on their next sign-in that they do not have
 * access rather than that they are still waiting. Either answer is audited, and either can be
 * reversed from the directory below.
 */
function AccessRequests({ requests, loading, busyEmail, onAnswer }: AccessRequestsProps) {
  return (
    <section
      aria-labelledby="access-requests-title"
      style={{
        background: color.surface,
        border: `1px solid ${requests.length > 0 ? color.warningBorder : color.border}`,
        borderRadius: radius['3xl'],
        padding: '16px 20px',
        marginBottom: 20,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
        <h2
          id="access-requests-title"
          style={{ margin: 0, fontSize: fontSize.control, fontWeight: 600, color: color.ink }}
        >
          Access requests
        </h2>
        {requests.length > 0 ? (
          <span style={{ fontSize: fontSize.caption, fontWeight: 600, color: color.noticeInk }}>
            {requests.length} waiting
          </span>
        ) : null}
      </div>
      <div style={{ fontSize: fontSize.caption, color: color.inkMuted, marginTop: 2 }}>
        People who have signed in and are waiting for you to grant them access. Until you do,
        they see a message asking them to contact an administrator.
      </div>

      {loading ? (
        <div style={{ marginTop: 12 }}>
          <Skeleton height={48} />
        </div>
      ) : requests.length === 0 ? (
        <div style={{ marginTop: 12, fontSize: fontSize.body, color: color.inkSubtle }}>
          No one is waiting for access.
        </div>
      ) : (
        <ul style={{ listStyle: 'none', margin: '12px 0 0', padding: 0 }}>
          {requests.map((user) => {
            const busy = busyEmail === user.email;
            const who = user.displayName ?? user.email;
            return (
              <li
                key={user.email}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 12,
                  flexWrap: 'wrap',
                  padding: '10px 0',
                  borderTop: `1px solid ${color.border}`,
                }}
              >
                <div style={{ minWidth: 0, marginRight: 'auto' }}>
                  <div
                    style={{ fontSize: fontSize.bodyStrong, fontWeight: 600, color: color.ink }}
                  >
                    {who}
                  </div>
                  <div style={{ fontSize: fontSize.caption, color: color.inkFaint }}>
                    {user.displayName ? `${user.email} · ` : ''}requested{' '}
                    {formatDate(user.firstSeenAt)}
                  </div>
                </div>
                <button
                  type="button"
                  disabled={busy}
                  aria-label={`Grant ${who} access`}
                  onClick={() => onAnswer(user.email, true)}
                  style={buttonStyle(busy)}
                >
                  {busy ? 'Saving…' : 'Grant access'}
                </button>
                <button
                  type="button"
                  disabled={busy}
                  aria-label={`Decline ${who}'s request`}
                  onClick={() => onAnswer(user.email, false)}
                  style={buttonStyle(busy)}
                >
                  Decline
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

// ---------------------------------------------------------------------------
// Local data
// ---------------------------------------------------------------------------

/**
 * The client store's controls (owner request 2026-08-28).
 *
 * The app keeps a copy of the read model in the browser and fills it in the background on
 * sign-in, so a page is usually answered before it is opened (`store/dataStore.ts`). Two buttons,
 * and the difference between them is **which cache they clear**:
 *
 * | | Browser copy | Server cache (Redis) | The snapshot itself |
 * | - | - | - | - |
 * | **Refresh** | marked stale, refetched | untouched | untouched |
 * | **Hard refresh** | discarded, refetched | purged for this snapshot | untouched |
 *
 * ⚠ **Neither one re-runs the ingest, and no button here can.** Principle II forbids a
 * request-time path into the daily job — that is why it is a Cloud Run *Job* with no HTTP surface
 * at all. So the panel states what moves the figures and what does not, rather than offering a
 * control that would look like it fetched new business data and quietly did not. Forcing new
 * upstream data is `gcloud run jobs execute bob-ingest`, run by an operator.
 *
 * Both buttons report what they did. A refresh whose outcome is invisible is one a reader presses
 * twice.
 */
function DataPanel() {
  const { refresh, hardRefresh } = useStoreActions();
  const { progress, stats } = useStoreStatus();

  const [busy, setBusy] = useState<'refresh' | 'hard' | null>(null);
  const [outcome, setOutcome] = useState<{ ok: boolean; message: string } | null>(null);

  async function run(which: 'refresh' | 'hard') {
    setBusy(which);
    setOutcome(null);
    try {
      setOutcome(which === 'refresh' ? await refresh() : await hardRefresh());
    } finally {
      setBusy(null);
    }
  }

  const filling = progress.phase === 'running';
  const oldest = stats.oldestFetchedAt;
  // The store's own rule, read from the store rather than restated: a constant repeated in prose
  // is a constant that drifts.
  const expiresInHours = oldest
    ? Math.max(0, (oldest + TTL_MS - Date.now()) / 3_600_000)
    : null;

  return (
    <section
      aria-label="Local data"
      style={{
        background: color.surface,
        border: `1px solid ${color.border}`,
        borderRadius: radius['3xl'],
        padding: '16px 20px',
        marginBottom: 20,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <div style={{ marginRight: 'auto' }}>
          <div style={{ fontSize: fontSize.control, fontWeight: 600, color: color.ink }}>
            Local data
          </div>
          <div style={{ fontSize: fontSize.caption, color: color.inkMuted, marginTop: 2 }}>
            {filling
              ? `Fetching in the background — ${progress.completed} of ${progress.total}${
                  progress.stage ? ` · ${progress.stage}` : ''
                }`
              : `${stats.entries} data sets held${
                  expiresInHours === null
                    ? ''
                    : `, oldest expires in ${expiresInHours.toFixed(1)}h`
                }`}
          </div>
        </div>

        <button
          type="button"
          disabled={busy !== null}
          onClick={() => void run('refresh')}
          title="Refetch every data set from the server and replace the browser's copy. The server's own cache is left alone."
          style={buttonStyle(busy !== null)}
        >
          {busy === 'refresh' ? 'Refreshing…' : 'Refresh'}
        </button>

        <button
          type="button"
          disabled={busy !== null}
          onClick={() => void run('hard')}
          title="Also clear the server's cached responses for this snapshot, so every figure is recomputed from the database. It does not re-run the daily data load."
          style={buttonStyle(busy !== null)}
        >
          {busy === 'hard' ? 'Clearing…' : 'Hard refresh'}
        </button>
      </div>

      {outcome ? (
        <div
          role="status"
          style={{
            marginTop: 12,
            fontSize: fontSize.captionStrong,
            color: outcome.ok ? color.inkMintStrong : color.noticeInk,
            background: outcome.ok ? color.washMint : color.warningBg,
            border: `1px solid ${outcome.ok ? color.borderMint : color.warningBorder}`,
            borderRadius: radius.xl,
            padding: '8px 12px',
          }}
        >
          {outcome.message}
        </div>
      ) : null}

      <div
        style={{
          marginTop: 12,
          fontSize: fontSize.caption,
          color: color.inkSubtle,
          lineHeight: 1.5,
          maxWidth: 760,
        }}
      >
        The browser holds each data set for six hours, then refetches it the next time a page
        asks. Neither button loads new data from the warehouse — the figures change when the
        daily refresh runs, and the as-of date in every page header is what says when that was.
        {stats.persistAvailable ? null : (
          <>
            {' '}
            <strong>This browser&rsquo;s storage is full</strong>, so data is being kept for
            this session only and will be refetched next time.
          </>
        )}
      </div>

      {/*
        The second of the two admin capabilities Constitution 2.0.0 enumerates (spec 006).
        ⚠ Read-only, transcript text only, and every read audited with both addresses.
      */}
      <AdminThreadViewer />
    </section>
  );
}

function buttonStyle(disabled: boolean) {
  return {
    fontSize: fontSize.body,
    fontWeight: 600,
    padding: '6px 12px',
    borderRadius: radius.xl,
    border: `1px solid ${disabled ? color.borderChip : color.borderStrong}`,
    background: disabled ? color.surfaceChip : color.surface,
    color: disabled ? color.inkFaint : color.inkRow,
    cursor: disabled ? 'not-allowed' : 'pointer',
    fontFamily: 'inherit',
  } as const;
}
